import { createFileRoute } from '@tanstack/react-router';
import { Badge, Button } from '@mantine/core';
import { IconBuildingCommunity, IconMapPin } from '@tabler/icons-react';
import { useState } from 'react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { QuickCreateButton, type FieldDef } from '@/shared/components/QuickCreateButton';
import { SiteSharingDrawer, type SharingSite } from '@/features/workforce/SiteSharingDrawer';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Site extends Denormalized {
  type: 'erp-sites';
  name?: string;
  address?: string;
  addressNumber?: number;
  city?: string;
  province?: string;
  region?: string;
  zip?: string;
  state?: string;
  phone?: string;
  email?: string;
  siteType?: string;
  // BE v1.17.1 (Site Sharing): i 4 flag legale/amministrativa/fatturazione/principale
  // NON sono più su erp-sites — vivono sui mapping (erp-site-tenant-mappings) ed è
  // gestibile dal pulsante «Condivisione». tenant 1:1 → primaryTenant.
}

const SITE_TYPE_OPTIONS = [
  { value: 'office', label: 'Ufficio' },
  { value: 'warehouse', label: 'Magazzino' },
  { value: 'sales_point', label: 'Punto vendita' },
  { value: 'factory', label: 'Fabbrica' },
  { value: 'workshop', label: 'Officina' },
  { value: 'showroom', label: 'Showroom' },
  { value: 'construction_site', label: 'Cantiere' },
  { value: 'laboratory', label: 'Laboratorio' },
  { value: 'representation', label: 'Rappresentanza' },
  { value: 'other', label: 'Altro' },
];

const SITE_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  SITE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

const FIELDS: FieldDef[] = [
  { name: 'name', label: 'Nome sede', required: true },
  { name: 'siteType', label: 'Tipo', kind: 'select', options: SITE_TYPE_OPTIONS, defaultValue: 'office' },
  { name: 'address', label: 'Indirizzo' },
  { name: 'addressNumber', label: 'Civico', kind: 'number' },
  { name: 'city', label: 'Città' },
  { name: 'province', label: 'Provincia' },
  { name: 'region', label: 'Regione' },
  { name: 'zip', label: 'CAP' },
  { name: 'state', label: 'Paese (ISO2)', defaultValue: 'IT' },
  { name: 'phone', label: 'Telefono' },
  { name: 'email', label: 'Email' },
];

export const Route = createFileRoute('/_app/workforce/sites')({ component: SitesPage });

function SitesPage() {
  const [sharing, setSharing] = useState<SharingSite | null>(null);
  return (
    <>
      <ResourceListPage<Site>
        title=""
        type="erp-sites"
        columns={[
          { accessorKey: 'name', header: 'Sede' },
          {
            accessorKey: 'siteType',
            header: 'Tipo',
            size: 130,
            Cell: ({ cell }) => {
              const v = cell.getValue<string | undefined>();
              return v ? <Badge variant="light" size="sm">{SITE_TYPE_LABEL[v] ?? v}</Badge> : null;
            },
          },
          { accessorKey: 'address', header: 'Indirizzo' },
          { accessorKey: 'city', header: 'Città' },
          { accessorKey: 'province', header: 'Prov.', size: 80 },
          { accessorKey: 'zip', header: 'CAP', size: 90 },
          {
            id: 'sharing',
            header: '',
            size: 150,
            enableSorting: false,
            enableColumnFilter: false,
            accessorFn: (row) => row,
            Cell: ({ row }) => (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconBuildingCommunity size={14} />}
                onClick={() => setSharing({ id: row.original.id, name: row.original.name })}
              >
                Condivisione
              </Button>
            ),
          },
        ]}
        searchField="name"
        emptyMessage="Nessuna sede registrata"
        emptyDescription="Crea la prima sede con il bottone «Nuova sede» nella toolbar."
        emptyIcon={IconMapPin}
        editFields={FIELDS}
        editTitle="Modifica sede"
        enableDelete
        deleteCascade
        getRowLabel={(r) => r.name ?? r.id}
        toolbarRight={<QuickCreateButton type="erp-sites" title="Nuova sede" fields={FIELDS} />}
      />
      <SiteSharingDrawer site={sharing} onClose={() => setSharing(null)} />
    </>
  );
}
